/**
 * Page thumbnail / OG-image upload endpoints
 *
 * Wraps the multipart `PUT /pages/:id/thumbnails`, `PUT /pages/:id/og-image`
 * and `POST /:workspaceId/reports/:moduleId/thumbnails` endpoints.
 */

import { env } from '@/lib/env'
import type { PageThumbnailMetadata, PageOgImageMetadata } from './objects.types'

/**
 * Error thrown by the thumbnail endpoints with the structured server-side
 * error code (e.g. `STALE_THUMBNAIL`, `VERSION_CONFLICT`) so the caller
 * can bucket failures for telemetry without re-parsing the body.
 */
export class ThumbnailUploadError extends Error {
  readonly status: number
  readonly code: string | null
  readonly body: unknown

  constructor(message: string, status: number, code: string | null, body: unknown) {
    super(message)
    this.name = 'ThumbnailUploadError'
    this.status = status
    this.code = code
    this.body = body
  }
}

async function parseThumbnailErrorBody(response: Response): Promise<{ code: string | null; body: unknown; message: string }> {
  let body: unknown = undefined
  let code: string | null = null
  let message = `HTTP ${response.status}`
  try {
    const contentType = response.headers.get('Content-Type') ?? ''
    if (contentType.includes('application/json')) {
      const json = await response.json()
      body = json
      if (json && typeof json === 'object') {
        if (typeof (json as any).error === 'string') code = (json as any).error
        if (typeof (json as any).message === 'string') message = (json as any).message
        else if (code) message = code
      }
    } else {
      const text = await response.text()
      body = text
      if (text) message = text
    }
  } catch {
    // ignore — we'll fall back to the status-line message
  }
  return { code, body, message }
}

/**
 * Upload a captured 288x216 PNG thumbnail for an object-backed page.
 *
 * Wraps `PUT /pages/:objectId/thumbnails`. Uses `multipart/form-data`
 * with a `thumbnail` (Blob) part + `contentHash` (sha256 hex of the
 * full page html) + optional `captureDelayMs` metadata. The server
 * rejects with 409 `STALE_THUMBNAIL` if the page html changed between
 * capture and upload, and 409 `VERSION_CONFLICT` if `If-Match` doesn't
 * match the object's current version (after one server-side retry).
 */
export async function uploadPageListThumbnail(
  token: string,
  objectId: string,
  input: {
    png: Blob
    contentHash: string
    captureDelayMs?: number
    ifMatch: string
  },
): Promise<{
  thumbnail: PageThumbnailMetadata
  object: any
}> {
  const formData = new FormData()
  formData.append('thumbnail', input.png)
  formData.append('contentHash', input.contentHash)
  if (typeof input.captureDelayMs === 'number' && Number.isFinite(input.captureDelayMs)) {
    formData.append('captureDelayMs', String(input.captureDelayMs))
  }

  const url = `${env.apiBaseUrl}/pages/${encodeURIComponent(objectId)}/thumbnails`
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'If-Match': input.ifMatch,
    },
    body: formData,
    credentials: 'include',
  })

  if (!response.ok) {
    const { code, body, message } = await parseThumbnailErrorBody(response)
    throw new ThumbnailUploadError(message, response.status, code, body)
  }

  return (await response.json()) as { thumbnail: PageThumbnailMetadata; object: any }
}

/**
 * Upload a captured 1200x630 PNG OG image for an object-backed page.
 *
 * Wraps `PUT /pages/:objectId/og-image`. Uses `multipart/form-data` with an
 * `og` (Blob) part + `contentHash` (sha256 hex of the full page html) and
 * a required `If-Match` header for optimistic concurrency. The server
 * stores the bytes to `attrs.ogImage` and the next `POST /publish` copies
 * them to a public R2 key + writes `attrs.publish.ogImage`.
 *
 * Field name is `og` (not `thumbnail`) — the backend distinguishes the
 * two endpoints by the multipart field, not by the URL prefix.
 *
 * Failures map to `ThumbnailUploadError` with the structured server-side
 * error code (`STALE_OG`, `VERSION_CONFLICT`, etc.) so the caller can
 * bucket failures for telemetry without re-parsing the body. Callers that
 * publish should treat any failure here as non-fatal — the publish
 * endpoint falls back to a generic server-rendered OG image.
 */
export async function uploadPageOgImage(
  token: string,
  objectId: string,
  input: {
    png: Blob
    contentHash: string
    ifMatch: string
  },
): Promise<{
  ogImage: PageOgImageMetadata
  object: any
}> {
  const formData = new FormData()
  formData.append('og', input.png)
  formData.append('contentHash', input.contentHash)

  const url = `${env.apiBaseUrl}/pages/${encodeURIComponent(objectId)}/og-image`
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'If-Match': input.ifMatch,
    },
    body: formData,
    credentials: 'include',
  })

  if (!response.ok) {
    const { code, body, message } = await parseThumbnailErrorBody(response)
    throw new ThumbnailUploadError(message, response.status, code, body)
  }

  return (await response.json()) as { ogImage: PageOgImageMetadata; object: any }
}

/**
 * Trigger server-side regeneration of a report module's list thumbnail.
 *
 * Wraps `POST /:workspaceId/reports/:moduleId/thumbnails`. No client bytes
 * are sent — the server reuses the existing `renderOgImage()` pipeline,
 * downscales/crops to 288x216 and stores the result on
 * `module.config.thumbnail`.
 */
export async function generateReportListThumbnail(
  token: string,
  moduleId: string,
  workspaceId: string,
): Promise<{
  thumbnail: PageThumbnailMetadata
  module: any
}> {
  const url = `${env.apiBaseUrl}/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(moduleId)}/thumbnails`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
    credentials: 'include',
  })

  if (!response.ok) {
    const { code, body, message } = await parseThumbnailErrorBody(response)
    throw new ThumbnailUploadError(message, response.status, code, body)
  }

  return (await response.json()) as { thumbnail: PageThumbnailMetadata; module: any }
}
